import { useMemo } from 'react';
import dayjs from 'dayjs';
import {
    type TimeEntryDocType,
    type FindingPlanumDocType,
    type FindingProfileDocType,
    type OverallDescriptionDocType,
    type AreaDescriptionDocType,
    type SampleDocType,
    type DrawingSheetDocType,
    type PhotoDocType,
    type FindDocType
} from '@ast-data/shared';
import { useProjectDocs } from '../../db/useScopedDocs';
import { useCrewKeyResolver } from '../projects/crewIdentity';
import { collectCrew, type CrewRecord } from '../projects/crewRecords';

import { compareText } from '../../utils/compare';

/** The Zeiterfassung rows that are a person's working day, not a machine's. */
const WORKER_ENTRIES = { entryType: 'worker' } as const;

export interface DiaryDayPerson {
    /** The crew key (app-user id when known, else the normalized name). */
    key: string;
    name: string;
    /** Source-type keys the person was picked up from (timeEntry, photo, …). */
    sources: string[];
    /** The day's records naming the person. */
    records: CrewRecord[];
}

/**
 * How many records of each kind carry the Tagebuch's day, derived from the SAME
 * criterion the links to each grid filter by, so a "3" cannot lead to an empty
 * table. `findings` counts DESCRIPTIONS (Planum/Profil): a Befund has no day of
 * its own, and the Befunde grid's date column is its descriptions'.
 */
export interface DiaryDayCounts {
    findings: number;
    finds: number;
    samples: number;
    drawingSheets: number;
    photos: number;
    overallDescriptions: number;
    areaDescriptions: number;
}

export interface DiaryDayInfo {
    /**
     * Earliest start and latest end across the day's Mitarbeiter-Zeiterfassung.
     * Null when no entry of the day has both times.
     */
    workSpan: { start: string; end: string } | null;
    /** The day's most common booked Pause; null when nobody booked one. */
    pauseMinutes: number | null;
    /** Everyone who recorded something for the project that day, by name. */
    people: DiaryDayPerson[];
    counts: DiaryDayCounts;
}

/** The local calendar day of an ISO datetime ('' when unparseable). */
function dayOf(value: string): string {
    if (!value) {
        return '';
    }
    const date = dayjs(value);
    return date.isValid() ? date.format('YYYY-MM-DD') : '';
}

/**
 * The one break to offer for a day the whole crew shares a Tagebuch line for.
 *
 * The span takes the outer bounds, but a Pause doesn't work that way — the
 * longest break is one person's, not the day's. So: the one MOST of the crew
 * booked, a tie going to the longer. Null when nobody booked one.
 */
export function commonPauseMinutes(
    entries: readonly { pauseMinutes?: number }[]
): number | null {
    const tally = new Map<number, number>();
    for (const entry of entries) {
        const minutes = Math.trunc(Number(entry.pauseMinutes));
        if (!Number.isFinite(minutes) || minutes <= 0) {
            continue;
        }
        tally.set(minutes, (tally.get(minutes) ?? 0) + 1);
    }
    let best: number | null = null;
    let bestCount = 0;
    for (const [minutes, count] of tally) {
        if (count > bestCount || (count === bestCount && minutes > best!)) {
            best = minutes;
            bestCount = count;
        }
    }
    return best;
}

/**
 * What the rest of the app already knows about the Tagebuch's day: the working
 * span from the Zeiterfassung, and everyone who recorded anything that day. The
 * same sources the Grabungsmannschaft aggregates, narrowed to the single day —
 * minus the project's own Grabungsleitung/Autoren, who are crew by the Maßnahme
 * rather than by having been on site.
 */
export function useDiaryDay(
    projectId: string,
    recordedAt: string
): DiaryDayInfo {
    const day = dayOf(recordedAt);

    // Worker entries only — a digger's hours are not a person's working day.
    const { results: timeEntries } = useProjectDocs<TimeEntryDocType>(
        'timeEntries',
        projectId,
        WORKER_ENTRIES
    );
    const { results: planums } = useProjectDocs<FindingPlanumDocType>(
        'finding_planums',
        projectId
    );
    const { results: profiles } = useProjectDocs<FindingProfileDocType>(
        'finding_profiles',
        projectId
    );
    const { results: overallDescriptions } =
        useProjectDocs<OverallDescriptionDocType>(
            'overall_descriptions',
            projectId
        );
    const { results: areaDescriptions } =
        useProjectDocs<AreaDescriptionDocType>('area_descriptions', projectId);
    const { results: drawingSheets } = useProjectDocs<DrawingSheetDocType>(
        'drawing_sheets',
        projectId
    );
    const { results: photos } = useProjectDocs<PhotoDocType>(
        'photos',
        projectId
    );
    const { results: finds } = useProjectDocs<FindDocType>('finds', projectId);
    const { results: samples } = useProjectDocs<SampleDocType>(
        'samples',
        projectId
    );

    const resolver = useCrewKeyResolver();

    const dayTimeEntries = useMemo(
        () => (day ? timeEntries.filter((entry) => entry.date === day) : []),
        [timeEntries, day]
    );

    const workSpan = useMemo(() => {
        let start = '';
        let end = '';
        for (const entry of dayTimeEntries) {
            if (entry.startTime && (!start || entry.startTime < start)) {
                start = entry.startTime;
            }
            if (entry.endTime && (!end || entry.endTime > end)) {
                end = entry.endTime;
            }
        }
        // Only a complete span is worth offering — half of one would be copied
        // into the wrong field.
        return start && end ? { start, end } : null;
    }, [dayTimeEntries]);

    const pauseMinutes = useMemo(
        () => commonPauseMinutes(dayTimeEntries),
        [dayTimeEntries]
    );

    const people = useMemo<DiaryDayPerson[]>(() => {
        if (!day) {
            return [];
        }
        // Only the records of this very day count towards its Personal.
        const onDay = <T extends { recordedAt: string }>(rows: readonly T[]) =>
            rows.filter((row) => dayOf(row.recordedAt) === day);
        const collected = collectCrew(
            [
                ['timeEntry', dayTimeEntries],
                ['findingPlanum', onDay(planums)],
                ['findingProfile', onDay(profiles)],
                ['overallDescription', onDay(overallDescriptions)],
                ['areaDescription', onDay(areaDescriptions)],
                ['drawingSheet', onDay(drawingSheets)],
                ['photo', onDay(photos)],
                ['find', onDay(finds)],
                ['sample', onDay(samples)]
            ],
            resolver
        );

        return [...collected.entries()]
            .map(([key, entry]) => ({
                key,
                name: resolver.displayName(entry.name, key),
                sources: [...entry.sources],
                records: entry.records
            }))
            .sort((firstPerson, secondPerson) =>
                compareText(firstPerson.name, secondPerson.name)
            );
    }, [
        day,
        resolver,
        dayTimeEntries,
        planums,
        profiles,
        overallDescriptions,
        areaDescriptions,
        drawingSheets,
        photos,
        finds,
        samples
    ]);

    const counts = useMemo<DiaryDayCounts>(() => {
        if (!day) {
            return {
                findings: 0,
                finds: 0,
                samples: 0,
                drawingSheets: 0,
                photos: 0,
                overallDescriptions: 0,
                areaDescriptions: 0
            };
        }
        const onThisDay = (rows: readonly { recordedAt: string }[]) =>
            rows.filter((row) => dayOf(row.recordedAt) === day).length;
        return {
            findings: onThisDay(planums) + onThisDay(profiles),
            finds: onThisDay(finds),
            samples: onThisDay(samples),
            drawingSheets: onThisDay(drawingSheets),
            photos: onThisDay(photos),
            overallDescriptions: onThisDay(overallDescriptions),
            areaDescriptions: onThisDay(areaDescriptions)
        };
    }, [
        day,
        planums,
        profiles,
        finds,
        samples,
        drawingSheets,
        photos,
        overallDescriptions,
        areaDescriptions
    ]);

    return { workSpan, pauseMinutes, people, counts };
}
